import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getFilteredMenuItems, getMenuItems, getUserData, USER_LEVELS } from '../config/menuItems';
import { getDomainLogo } from '../utils/domainConfig';

const logo = getDomainLogo();

const Icon = ({ name }) => {
  if (name === 'dashboard') {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
      </svg>
    );
  }
  if (name === 'inbox') {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
      </svg>
    );
  }
  if (name === 'contacts') {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
      </svg>
    );
  }
  if (name === 'navigation') {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
      </svg>
    );
  }
  if (name === 'data-entry') {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
      </svg>
    );
  }
  if (name === 'data-display') {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z"/>
      </svg>
    );
  }
  if (name === 'feedback') {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 14h-3v3h-2v-3H8v-2h3v-3h2v3h3v2zm-3-7V3.5L18.5 9H13z"/>
      </svg>
    );
  }
  if (name === 'attachment') {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
      </svg>
    );
  }
  if (name === 'menu-fold') {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
        <path d="M3 18h13v-2H3v2zm0-5h10v-2H3v2zm0-7v2h13V6H3zm18 9.59L17.42 12 21 8.41 19.59 7l-5 5 5 5L21 15.59z"/>
      </svg>
    );
  }
  if (name === 'menu-unfold') {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
        <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
      </svg>
    );
  }
  return null;
};

const ArrowIcon = ({ isOpen }) => (
  <svg
    className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-90 text-[var(--accent)]' : 'text-[var(--chrome-text-3)]'}`}
    fill="currentColor"
    viewBox="0 0 24 24"
  >
    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
  </svg>
);

const Dot = ({ active }) => (
  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors ${active ? 'bg-[var(--accent)]' : 'bg-[#9a9aa8]'}`} />
);

const SubMenuItem = ({ item, isActive, onClick }) => {
  const base = 'flex items-center gap-2.5 py-2 pl-9 pr-4 text-[13px] font-medium transition-all duration-150';
  const active = base + ' text-[var(--accent)] bg-[var(--accent)]/10';
  const inactive = base + ' text-[var(--chrome-text-2)] hover:text-[var(--chrome-text)] hover:bg-black/5';

  if (item.external) {
    return (
      <a href={item.path} target="_blank" rel="noopener noreferrer" className={inactive} onClick={onClick}>
        <Dot active={false} />
        {item.label}
      </a>
    );
  }

  return (
    <Link to={item.path} className={isActive ? active : inactive} onClick={onClick}>
      <Dot active={isActive} />
      {item.label}
    </Link>
  );
};

const SubMenuItemExpanded = ({ item, isActive, onNavigate }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const base = 'flex items-center gap-2.5 py-2 pl-9 pr-4 text-[13px] font-medium transition-all duration-150';
  const active = base + ' text-[var(--accent)] bg-[var(--accent)]/10';
  const inactive = base + ' text-[var(--chrome-text-2)] hover:text-[var(--chrome-text)] hover:bg-black/5';

  const hasChildren = item.children && item.children.length > 0;
  const isChildActive = hasChildren && item.children.some(child => location.pathname === child.path);

  if (hasChildren) {
    const btnClass = isChildActive || isOpen
      ? 'w-full flex items-center justify-between py-2 pl-9 pr-4 text-[13px] font-medium text-[var(--accent)] bg-[var(--accent)]/10 transition-all duration-150'
      : 'w-full flex items-center justify-between py-2 pl-9 pr-4 text-[13px] font-medium text-[var(--chrome-text-2)] hover:text-[var(--chrome-text)] hover:bg-black/5 transition-all duration-150';

    return (
      <div>
        <button onClick={() => setIsOpen(!isOpen)} className={btnClass}>
          <span className="flex items-center gap-2.5">
            <Dot active={isChildActive} />
            {item.label}
          </span>
          <ArrowIcon isOpen={isOpen} />
        </button>
        {isOpen && (
          <div className="bg-black/5">
            {item.children.map(child => {
              const childActive = location.pathname === child.path;
              const childClass = childActive
                ? 'flex items-center gap-2 py-[7px] pl-14 pr-4 text-[12px] font-medium text-[var(--accent)] bg-[var(--accent)]/10 transition-all duration-150'
                : 'flex items-center gap-2 py-[7px] pl-14 pr-4 text-[12px] font-medium text-[var(--chrome-text-3)] hover:text-[var(--chrome-text)] hover:bg-black/5 transition-all duration-150';
              return (
                <Link key={child.key} to={child.path} className={childClass} onClick={onNavigate}>
                  <span className={`w-1 h-1 rounded-full flex-shrink-0 ${childActive ? 'bg-[var(--accent)]' : 'bg-[#c9c9d3]'}`} />
                  {child.label}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (item.external) {
    return (
      <a href={item.path} target="_blank" rel="noopener noreferrer" className={inactive}>
        <Dot active={false} />
        {item.label}
      </a>
    );
  }

  return (
    <Link to={item.path} className={isActive ? active : inactive} onClick={onNavigate}>
      <Dot active={isActive} />
      {item.label}
    </Link>
  );
};

const MenuItem = ({
  item, location, collapsed,
  activePopup, setActivePopup, setPopupPosition,
  openDropdown, setOpenDropdown, onNavigate,
}) => {
  const itemRef = useRef(null);
  const hasChildren = item.children && item.children.length > 0;
  const isActive = location.pathname === item.path;
  const isChildActive = hasChildren && item.children.some(child => location.pathname === child.path);
  const isPopupOpen = activePopup === item.key;
  const isOpen = openDropdown.includes(item.key);

  const handleCollapsedClick = () => {
    if (hasChildren) {
      const rect = itemRef.current.getBoundingClientRect();
      setPopupPosition({ top: rect.top, left: 80 });
      setActivePopup(isPopupOpen ? null : item.key);
    }
  };

  const handleExpandedClick = () => {
    if (hasChildren) {
      if (isOpen) {
        setOpenDropdown(openDropdown.filter(k => k !== item.key));
      } else {
        setOpenDropdown([...openDropdown, item.key]);
      }
    }
  };

  // ── Collapsed mode ───────────────────────────────────────────────────────────
  if (collapsed) {
    const highlighted = isActive || isChildActive || isPopupOpen;
    const iconWrap = `relative flex items-center justify-center py-3 px-2 transition-all duration-150 cursor-pointer
      ${highlighted ? 'text-[var(--accent)] bg-[var(--accent)]/10' : 'text-[var(--chrome-text-2)] hover:text-[var(--chrome-text)] hover:bg-black/5'}`;

    const inner = (
      <>
        {highlighted && (
          <span className="absolute left-0 top-1.5 bottom-1.5 w-1 bg-gradient-to-b from-[var(--accent)] to-[var(--accent-3)] rounded-r shadow-[0_0_10px_rgba(192,43,100,0.7)]" />
        )}
        <Icon name={item.icon} />
      </>
    );

    if (hasChildren) {
      return (
        <div ref={itemRef}>
          <div className={iconWrap} onClick={handleCollapsedClick} title={item.label}>
            {inner}
          </div>
        </div>
      );
    }
    if (item.external) {
      return (
        <a href={item.path} target="_blank" rel="noopener noreferrer" className={iconWrap} title={item.label}>
          {inner}
        </a>
      );
    }
    return (
      <Link to={item.path} className={iconWrap} title={item.label}>
        {inner}
      </Link>
    );
  }

  // ── Expanded mode ────────────────────────────────────────────────────────────
  if (hasChildren) {
    const btnClass = `w-full flex items-center justify-between py-2.5 px-4 text-[11px] font-bold tracking-widest uppercase transition-all duration-150
      ${isChildActive ? 'text-white bg-[var(--accent)]' : 'text-[var(--chrome-text)] hover:text-[var(--accent)] hover:bg-black/5'}`;

    return (
      <div>
        <button onClick={handleExpandedClick} className={btnClass}>
          <span className="flex items-center gap-3">
            <span className={isChildActive ? 'text-white' : 'text-[var(--chrome-text-2)]'}>
              <Icon name={item.icon} />
            </span>
            {item.label}
          </span>
          <ArrowIcon isOpen={isOpen} />
        </button>
        {isOpen && (
          <div className="border-l border-black/10 ml-[22px]">
            {item.children.map(child => (
              <SubMenuItemExpanded
                key={child.key}
                item={child}
                isActive={location.pathname === child.path}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (item.external) {
    return (
      <a href={item.path} target="_blank" rel="noopener noreferrer"
        className="relative flex items-center gap-3 py-2.5 px-4 text-[13px] font-medium text-[var(--chrome-text)] hover:text-[var(--accent)] hover:bg-black/5 transition-all duration-150">
        <Icon name={item.icon} />
        {item.label}
      </a>
    );
  }

  return (
    <Link
      to={item.path}
      className={`relative flex items-center gap-3 py-2.5 px-4 text-[13px] font-semibold transition-all duration-200
        ${isActive
          ? 'text-white bg-[var(--accent)]'
          : 'text-[var(--chrome-text)] hover:text-[var(--accent)] hover:bg-black/5'
        }`}
    >
      <span className={isActive ? 'text-white' : 'text-[var(--chrome-text-2)]'}>
        <Icon name={item.icon} />
      </span>
      {item.label}
    </Link>
  );
};

const Sidebar = ({ isOpen, onClose, collapsed, onToggleCollapse }) => {
  const location = useLocation();
  const [activePopup, setActivePopup] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 80 });
  const [openDropdown, setOpenDropdown] = useState([]);
  const [, setForceUpdate] = useState(0);

  const { user } = useSelector(state => state.auth);
  const userLevel = user?.userTypeInfo || USER_LEVELS.CLIENT;
  const isFinance = getUserData()?.userType === 'Finance';
  const filteredMenuItems = isFinance ? getFilteredMenuItems(1) : getFilteredMenuItems(userLevel);

  useEffect(() => {
    const handler = ev => { if (ev.key === 'user_data') setForceUpdate(n => n + 1); };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  useEffect(() => { setActivePopup(null); }, [location.pathname]);

  const handleOverlayClick = () => { setActivePopup(null); onClose(); };
  const closePopup = () => setActivePopup(null);
  const handleSubMenuNavigation = () => { if (window.innerWidth < 1024) onClose(); };

  const activePopupItem = filteredMenuItems.find(item => item.key === activePopup);

  let sidebarClass =
    'fixed top-0 left-0 h-full z-50 transition-all duration-300 overflow-y-auto overflow-x-hidden ' +
    'glass-dark shadow-[8px_0_40px_-12px_rgba(0,0,0,0.15)] border-r border-black/10';
  sidebarClass += isOpen ? ' translate-x-0 w-64' : ' -translate-x-full w-64';
  sidebarClass += ' lg:translate-x-0';
  sidebarClass += collapsed ? ' lg:w-20' : ' lg:w-64';

  return (
    <React.Fragment>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={handleOverlayClick}
        />
      )}

      {/* Desktop popup backdrop */}
      {activePopup && collapsed && (
        <div className="fixed inset-0 z-[55] hidden lg:block" onClick={closePopup} />
      )}

      {/* Collapsed popup menu */}
      {activePopup && collapsed && activePopupItem?.children && (
        <div
          className="fixed bg-white border border-slate-200 rounded-xl shadow-2xl py-2 min-w-[220px] z-[60] hidden lg:block"
          style={{ top: popupPosition.top + 'px', left: popupPosition.left + 'px' }}
        >
          <div className="px-4 pb-2 mb-1 border-b border-slate-200">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
              {activePopupItem.label}
            </p>
          </div>
          {activePopupItem.children.map(child => (
            <SubMenuItem
              key={child.key}
              item={child}
              isActive={location.pathname === child.path}
              onClick={closePopup}
            />
          ))}
        </div>
      )}

      <aside className={sidebarClass}>
        {/* Sidebar header */}
        <div className="flex items-center h-16 px-4 border-b border-black/10">
          {collapsed ? (
            <button
              onClick={onToggleCollapse}
              className="text-[var(--chrome-text-2)] hover:text-[var(--chrome-text)] w-full flex justify-center transition-colors"
            >
              <Icon name="menu-unfold" />
            </button>
          ) : (
            <React.Fragment>
              <button
                onClick={onToggleCollapse}
                className="text-[var(--chrome-text-2)] hover:text-[var(--chrome-text)] hidden lg:block transition-colors p-1 rounded-md hover:bg-black/5"
              >
                <Icon name="menu-fold" />
              </button>
              <button
                onClick={onClose}
                className="text-[var(--chrome-text-2)] hover:text-[var(--chrome-text)] lg:hidden transition-colors p-1 rounded-md hover:bg-black/5"
              >
                <Icon name="menu-unfold" />
              </button>
              <Link to="/dashboard" className="ml-auto">
                <img src={logo} alt="logo" className="h-[48px]" />
              </Link>
            </React.Fragment>
          )}
        </div>

        {/* Navigation */}
        <nav className="py-3 space-y-0.5">
          {filteredMenuItems.map(item => (
            <MenuItem
              key={item.key}
              item={item}
              location={location}
              collapsed={collapsed}
              activePopup={activePopup}
              setActivePopup={setActivePopup}
              setPopupPosition={setPopupPosition}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              onNavigate={handleSubMenuNavigation}
            />
          ))}
        </nav>
      </aside>
    </React.Fragment>
  );
};

export default Sidebar;
